import type { MouseEvent, ReactNode } from "react"

import { clsx } from "clsx"
import { Link } from "react-router-dom"

import { Drawer } from "~/components/ui-components/v2"

import { Icon } from "../../BatteryOnlyProposal/primitives"

/** The step drawer every pricing step opens in. */
export const PricingStepDrawer = ({
  title,
  onClose,
  contentClassName = `flex min-h-0 flex-col`,
  children,
}: {
  readonly title: string
  readonly onClose: () => void
  readonly contentClassName?: string
  readonly children: ReactNode
}): JSX.Element => (
  <Drawer
    open
    theme="dark"
    onClose={onClose}
    title={title}
    widthClass="sm:max-w-2xl"
    contentClassName={contentClassName}
  >
    {children}
  </Drawer>
)

/** The card heading most toggle cards use. */
export const ToggleCardTitle = ({
  children,
}: {
  readonly children: ReactNode
}): JSX.Element => (
  <span className="font-display text-[length:var(--step-0)] font-semibold text-foreground">
    {children}
  </span>
)

/**
 * A selectable card: the whole card toggles, and its heading is an
 * `aria-pressed` button. Clicks inside `[data-slot="card-field"]` (inputs and
 * their chrome) never toggle.
 */
export const ToggleCard = ({
  selected,
  disabled,
  dimmed = disabled,
  minHeightClass = `min-h-40`,
  title,
  toggleTestId,
  onToggle,
  children,
}: {
  readonly selected: boolean
  /** Blocks toggling. */
  readonly disabled: boolean
  /** Fades the card; defaults to `disabled`. */
  readonly dimmed?: boolean
  readonly minHeightClass?: string
  readonly title: ReactNode
  readonly toggleTestId?: string
  readonly onToggle: () => void
  readonly children?: ReactNode
}): JSX.Element => (
  <article
    onClick={(event) => {
      if (
        event.target instanceof Element &&
        event.target.closest(
          `input, textarea, select, label, [data-slot="card-field"], [data-slot="control"], [data-slot="addon"]`,
        )
      ) {
        return
      }
      if (!disabled) onToggle()
    }}
    className={clsx(
      `relative flex min-w-0 flex-col justify-between gap-3 rounded-lg border p-4`,
      minHeightClass,
      selected
        ? `border-bp-accent bg-bp-accent-soft`
        : `border-bp-border bg-bp-elevated`,
      dimmed ? `opacity-50` : null,
      disabled ? `cursor-default` : `cursor-pointer`,
    )}
  >
    <button
      type="button"
      data-testid={toggleTestId}
      aria-pressed={selected}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation()
        onToggle()
      }}
      className={clsx(
        `flex w-full items-start justify-between gap-3 text-left`,
        disabled ? `cursor-default` : null,
      )}
    >
      {title}
      {selected ? (
        <Icon name="check" className="size-4 shrink-0 text-bp-accent" />
      ) : (
        <span className="size-4 shrink-0 rounded-full border border-bp-border-strong" />
      )}
    </button>
    {children}
  </article>
)

/** Keeps clicks on a card's field from reaching the card's toggle. */
export const CardField = ({
  children,
}: {
  readonly children: ReactNode
}): JSX.Element => (
  <div
    data-slot="card-field"
    onClick={(event: MouseEvent) => event.stopPropagation()}
  >
    {children}
  </div>
)

/** The scrolling two-column card grid. */
export const ToggleCardGrid = ({
  testId,
  className = `grid min-h-0 min-w-0 flex-1 auto-rows-min grid-cols-2 gap-4 overflow-y-auto`,
  children,
}: {
  readonly testId: string
  readonly className?: string
  readonly children: ReactNode
}): JSX.Element => (
  <div className={className} data-testid={testId}>
    {children}
  </div>
)

const SECONDARY_BUTTON = `rounded-md border border-border-strong px-4 py-2 text-[length:var(--step--1)] font-medium text-foreground`

/** Footer for a step that has nothing to save yet (loading, error, gated). */
export const PricingStepCloseFooter = ({
  onClose,
}: {
  readonly onClose: () => void
}): JSX.Element => (
  <div className="mt-6 flex shrink-0 items-center border-t border-border pt-5">
    <button type="button" onClick={onClose} className={SECONDARY_BUTTON}>
      Close
    </button>
  </div>
)

/**
 * Close and Save, with the save's inline failure (and Retry) and its saved
 * status above them.
 */
export const PricingStepFooter = ({
  onClose,
  save,
  error,
  savedMessage,
}: {
  readonly onClose: () => void
  /** Omitted for a viewer who can't save. */
  readonly save: {
    readonly label: string
    readonly saving: boolean
    readonly disabled: boolean
    readonly onSave: () => void
  } | null
  readonly error: {
    readonly message: string
    /** Omitted when retrying the same save can't succeed. */
    readonly onRetry?: () => void
  } | null
  /** Shown once the save lands, until the step closes. */
  readonly savedMessage: string | null
}): JSX.Element => (
  <div className="mt-6 flex shrink-0 flex-col gap-3 border-t border-border pt-5">
    {error ? (
      <div
        role="alert"
        className="flex items-center justify-between gap-3 rounded-md border border-bp-danger/40 bg-bp-danger/10 px-3 py-2"
      >
        <span className="text-[length:var(--step--2)] text-danger">
          {error.message}
        </span>
        {error.onRetry ? (
          <button
            type="button"
            onClick={error.onRetry}
            className="shrink-0 rounded-md border border-bp-danger/50 px-3 py-1 text-[length:var(--step--2)] font-medium text-foreground"
          >
            Retry
          </button>
        ) : null}
      </div>
    ) : null}
    {savedMessage ? (
      <p
        role="status"
        className="text-[length:var(--step--2)] font-medium text-bp-success"
      >
        {savedMessage}
      </p>
    ) : null}
    <div className="flex items-center justify-between gap-4">
      <button type="button" onClick={onClose} className={SECONDARY_BUTTON}>
        Close
      </button>
      {save ? (
        <button
          type="button"
          disabled={save.disabled}
          onClick={save.onSave}
          className="rounded-md bg-bp-accent px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-on-accent hover:bg-bp-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {save.saving ? `Saving` : save.label}
        </button>
      ) : null}
    </div>
  </div>
)

/** A read that failed, with Retry. */
export const PricingLoadError = ({
  message,
  onRetry,
}: {
  readonly message: string
  readonly onRetry: () => void
}): JSX.Element => (
  <div className="flex flex-col gap-2">
    <p role="alert" className="text-[length:var(--step--2)] text-danger">
      {message}
    </p>
    <button
      type="button"
      onClick={onRetry}
      className="self-start rounded-md border border-border-strong px-3 py-1.5 text-[length:var(--step--2)] font-medium text-foreground"
    >
      Retry
    </button>
  </div>
)

export const PricingLoading = ({
  children,
}: {
  readonly children: string
}): JSX.Element => (
  <p className="text-[length:var(--step--1)] text-muted">{children}</p>
)

/** A step that needs stored pricing, before there is any. */
export const PricingRequiredNotice = ({
  message,
  detail,
  systemPricingPath,
}: {
  readonly message: string
  readonly detail: string
  readonly systemPricingPath: string
}): JSX.Element => (
  <div role="status" className="flex flex-col gap-3">
    <p className="text-[length:var(--step--1)] text-foreground">{message}</p>
    <p className="text-[length:var(--step--2)] text-muted">{detail}</p>
    <Link
      to={systemPricingPath}
      className="self-start font-medium text-foreground underline underline-offset-2 hover:text-foreground-soft"
    >
      Open System Pricing
    </Link>
  </div>
)
